import { KnowledgeArticle, createArticle } from '../types'

export const gettingStartedArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'platform-overview',
    title: 'Platform Overview',
    section: 'getting-started',
    summary: 'The GRC Platform helps manage compliance across PCI DSS, SOC 2, HIPAA, and ISO 27001 with controls management, evidence collection, cloud integrations, and risk management.',
    content: `
# Platform Overview

Welcome to the GRC (Governance, Risk, and Compliance) Platform. This platform helps you manage your organization's compliance requirements across multiple frameworks including PCI DSS, SOC 2, HIPAA, and ISO 27001.

## Key Features

- **Controls Management** - Track and manage compliance controls across frameworks
- **Evidence Collection** - Upload and organize compliance evidence
- **Cloud Integrations** - Automatically collect evidence from AWS and Azure
- **Document Management** - Create and manage policies and procedures
- **Risk Management** - Track and assess organizational risks
- **Vendor Risk (TPRM)** - Manage third-party vendor assessments
- **Vulnerability Scanning** - Scan your infrastructure for security issues
- **Key Risk Indicators** - Track and monitor KRIs with thresholds and alerts
- **Dark Web Monitoring** - Monitor for credential exposure and breaches

## Navigation

The main navigation is on the left sidebar:
- **Dashboard** - Overview of your compliance status
- **Tasks** - Action items and reminders
- **Controls** - Manage compliance controls
- **Mappings** - View control relationships across frameworks
- **Evidence** - Browse all collected evidence
- **Documents** - Policies and procedures
- **Integrations** - Cloud provider connections
- **Risks** - Risk register
- **KRIs** - Key Risk Indicators
- **TPRM** - Vendor management
- **Dark Web** - Threat monitoring
- **Scanners** - Vulnerability scanning
- **Assets** - Asset inventory
    `.trim(),
    tags: ['overview', 'introduction', 'navigation', 'features', 'compliance', 'grc'],
    relatedPages: ['/dashboard', '/'],
    icon: 'Book',
    order: 1,
  }),

  createArticle({
    id: 'quick-start',
    title: 'Quick Start Guide',
    section: 'getting-started',
    summary: 'Step-by-step guide to get started: review controls, connect cloud integrations, upload evidence, create policies, and track progress on the dashboard.',
    content: `
# Quick Start Guide

Follow these steps to get started with your GRC program:

## Step 1: Review Your Controls
Navigate to **Controls** to see your compliance requirements. Controls are pre-loaded based on your selected frameworks. Review each control to understand what's required.

## Step 2: Connect Cloud Integrations
Go to **Integrations** to connect your AWS or Azure accounts. This will automatically collect evidence like IAM configurations, security groups, and VM inventories.

## Step 3: Upload Evidence
For controls that require manual evidence, click on the control and use the **Upload Evidence** button. You can upload documents, screenshots, or any relevant files.

## Step 4: Create Policies
Go to **Documents** to create or upload your security policies. You can use AI to help generate policy templates based on your controls.

## Step 5: Track Progress
Use the **Dashboard** to monitor your overall compliance status. The dashboard shows evidence coverage, upcoming tasks, and areas needing attention.

**Tip:** Start with your highest-priority framework and focus on controls that are "Not Started" or missing evidence.
    `.trim(),
    tags: ['quick-start', 'tutorial', 'getting-started', 'onboarding'],
    relatedPages: ['/controls', '/integrations', '/documents', '/dashboard'],
    icon: 'Rocket',
    order: 2,
  }),
]
